import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { useToast } from "~/hooks/use-toast";
import type { ReceptionModificationRequest } from "~/types/receptionModification";

interface RejectReceptionModificationDialogProps {
  request: ReceptionModificationRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  isSubmitting: boolean;
}

export function RejectReceptionModificationDialog({
  request,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: RejectReceptionModificationDialogProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open, request?.id]);

  const handleConfirm = async () => {
    if (reason.trim() === "") {
      toast({
        title: "Motivo requerido",
        description: "Indica un motivo para rechazar la solicitud.",
        variant: "destructive",
      });
      return;
    }
    await onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Rechazar solicitud</DialogTitle>
          <DialogDescription>
            La modificación de{" "}
            <span className="font-medium">{request?.item_name}</span> se marcará
            como rechazada. La recepción no se modificará.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="reject-mod-reason">Motivo del rechazo</Label>
          <Textarea
            id="reject-mod-reason"
            placeholder="Explica por qué se rechaza esta solicitud…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmar rechazo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
